import * as React from 'react';

import moment from 'moment';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import { CardWatchNow } from '@coursera/cds-core';
import type { PageConfigComponent as PageConfigComponentTrackingData, Recommender } from '@coursera/event-pulse-types';

import type { Subdomain } from 'bundles/browse-common/legacy/components/Domain/types/SubDomain';
import type { DiscoveryCollectionRatingReviewEntity } from 'bundles/browse-common/legacy/types/DiscoveryCollections';
import type { EnrichedCatalogResultQuery } from 'bundles/browse-common/legacy/types/catalog-results';
import type { Domain } from 'bundles/browse/components/Domain/types/Domain';
import { getDiscoveryCollectionLabelWithDomainOrSubdomainName } from 'bundles/browse/components/Domain/utils/DomainUtils';
import {
  getDegreeCollectionLabel,
  getMastertrackCollectionLabel,
  getStackableDegreeCollectionLabel,
  getUCertCollectionLabel,
} from 'bundles/browse/constants';
import {
  COLLECTION_LABELS,
  COLLECTION_TYPENAMES,
  MAX_ITEMS_PER_ENTERPRISE_CAROUSEL,
  getEntityTypeFromTypename,
  getEntityTypeFromVariant,
  getLinkFromCarouselTypeName,
  getLinkFromProductVariant,
  getProductTypeFromTypename,
  getProductTypeFromVariant,
  getProgramLinkFromProductType,
} from 'bundles/collections-carousel/constants';
import type {
  CarouselCollection,
  CollectionEntity,
  CoreCollectionEntityWithMetadata,
  DegreeListsV3WithMetadata,
  DiscoveryCollectionRatingsReviewWithMetadata,
  DiscoveryCollectionWithMetadata,
} from 'bundles/collections-carousel/types/CarouselCollection';
import type {
  ProductCardClip,
  ProductCardCourse,
  ProductCardEntity,
  ProductCardSpecialization,
} from 'bundles/collections-carousel/types/ProductCardTypes';
import type { ClipItem, CollectionType, CourseSession } from 'bundles/enterprise-collections/components/CollectionItem';
import type { S12nSessionDatesQuery_OnDemandCourseSessionsV1Resource_multiGet_elements as S12nSession } from 'bundles/enterprise-legacy-learner-home/components/single-program/__generated__/S12nSessionDatesQuery';
import type { Product as CurriculumItem } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import { EnterpriseCreditEligibleBadge, EnterpriseExclusiveBadge } from 'bundles/product-card/components/Badges';
import { abbreviateLargeNumber, formatSession } from 'bundles/product-card/utils/productCardUtils';

import _t from 'i18n!nls/collections-carousel';

// For some reason the text for the "New" badge that is returned by the backend
// is in all caps unlike any of the other badges.
const badgeNewText = 'NEW';

export const extractProductRating = (entity: DiscoveryCollectionRatingReviewEntity) => {
  if (entity && 'productCard' in entity && entity.productCard) {
    const productCard = entity.productCard;
    if (
      'productTypeAttributes' in productCard &&
      productCard.productTypeAttributes &&
      'rating' in productCard.productTypeAttributes &&
      'reviewCount' in productCard.productTypeAttributes
    )
      return {
        avgProductRating: productCard.productTypeAttributes.rating,
        numProductRatings: productCard.productTypeAttributes.reviewCount,
      };
  }
  return null;
};

export const extractIsPathwayContent = (entity: DiscoveryCollectionRatingReviewEntity) => {
  if (entity?.productCard?.productTypeAttributes) {
    const productTypeAttributes = entity.productCard.productTypeAttributes;
    if ('isPathwayContent' in productTypeAttributes) return productTypeAttributes.isPathwayContent;
  }
  return false;
};

const mapPremiumCollectionsForCarousel = (
  collection: DegreeListsV3WithMetadata,
  index: number
): CarouselCollection | null => {
  if (!collection.entries || collection.entries.length === 0) {
    return null;
  }

  const entitiesFromEntries = collection.entries
    .map((entry, entryIndex) => {
      if (entry.degree) {
        const productType =
          getProductTypeFromTypename(collection.__typename) ||
          getProductTypeFromVariant(entry.degree.productVariant) ||
          getProductTypeFromTypename(entry.degree.__typename);

        const entityType =
          getEntityTypeFromTypename(collection.__typename) ||
          getEntityTypeFromVariant(entry.degree.productVariant) ||
          getEntityTypeFromTypename(entry.degree.__typename);

        return {
          id: entry.degree.id,
          name: entry.degree.name,
          slug: entry.degree.slug,
          imageUrl: entry.degree.thumbnailUrl || entry.degree.bannerImage,
          link: entry.degree.link,
          partners:
            entry.degree.partners &&
            entry.degree.partners.elements.map((partner) => {
              return {
                id: partner.id,
                logo: partner.squareLogo || partner.logo,
                name: partner.name,
              };
            }),
          partnerLogos:
            entry.degree.partners &&
            entry.degree.partners.elements.map((partner) => partner.squareLogo || partner.logo),
          productType,
          trackingData: {
            entityId: `${entityType}~${entry.degree.id}`,
            entityIndex: entryIndex,
            name: entry.degree.name,
            slug: entry.degree.slug,
            offeringType: productType,
            id: entry.degree.id,
          },
        };
      }

      return null;
    })
    .filter(Boolean) as CollectionEntity[];

  let premiumTitle = getDegreeCollectionLabel();
  let idForTracking = 'degree';
  // Also checking for the Collection Label here to handle mastertrack and u cert collections that come from catalog instead of disco collections.
  // We are doing this because LOHP still uses the catalog resource for collections, ticket to refactor to use disco collections and cleanup: https://coursera.atlassian.net/browse/CORDISC-1091
  if (
    collection?.label === COLLECTION_LABELS.STACKABLE_DEGREES ||
    collection?.label === COLLECTION_LABELS.STACKABLE_SPECIALIZATIONS
  ) {
    premiumTitle = getStackableDegreeCollectionLabel();
    idForTracking =
      collection?.label === COLLECTION_LABELS.STACKABLE_DEGREES ? 'stackable-degrees' : 'stackable-specializations';
  }
  if (
    collection?.__typename === COLLECTION_TYPENAMES.DEGREE_LIST_MASTERTRACK ||
    collection?.label === COLLECTION_LABELS.MASTERTRACKS
  ) {
    premiumTitle = getMastertrackCollectionLabel();
    idForTracking = 'mastertrack';
  }
  if (
    collection?.__typename === COLLECTION_TYPENAMES.DEGREE_LIST_UNIVERSITY_CERTIFICATE ||
    collection?.label === COLLECTION_LABELS.UNIVERSITY_CERTIFICATES
  ) {
    premiumTitle = getUCertCollectionLabel();
    idForTracking = 'university-certificate';
  }

  const collectionTitle = collection.collectionName?.() || premiumTitle;

  return {
    collectionId: collectionTitle,
    title: collectionTitle,
    entities: entitiesFromEntries,
    collectionTrackingData: {
      label: collectionTitle,
      collectionId: idForTracking,
      sectionId: idForTracking,
      collectionIndex: index,
      collectionName: collectionTitle,
    },
  };
};

const mapDiscoCollectionsForCarousel = (
  collection: DiscoveryCollectionRatingsReviewWithMetadata,
  index: number,
  domain?: Domain,
  subdomain?: Subdomain
): CarouselCollection | null => {
  if (!collection?.entities || collection.entities.length === 0) {
    return null;
  }

  const domainOrSubdomain = domain || subdomain;

  const splitCollectionId = collection.id.split(':');
  // DiscoveryCollection IDs have a format of `COLLECTION_TYPE:ID`, get each piece for tracking purposes.
  const collectionType = splitCollectionId?.[0];
  const collectionTypeId = splitCollectionId?.[1];

  const parsedEntities = collection.entities
    .map((entity, entityIndex) => {
      if (!entity) {
        return null;
      }

      const productType = getProductTypeFromTypename(entity.__typename);
      const entityType = getEntityTypeFromTypename(entity.__typename);

      const { avgProductRating, numProductRatings } = extractProductRating(entity) || {};
      const isPathwayContent = extractIsPathwayContent(entity);

      return {
        ...entity,
        link: collection.metadata?.isEnterprise
          ? getProgramLinkFromProductType(collection.metadata?.programSlug || '', undefined, entity.slug, productType)
          : entity.url,
        partnerLogos: entity.partners?.map((partner) => partner && partner.logo),
        productType,
        statusTags: entity.productCard?.badges,
        isNewContent: entity.productCard?.badges?.includes(badgeNewText),
        forceOpenInNewTab: collection.metadata?.isEnterprise,
        titleComponent: collection.metadata?.titleComponent,
        avgProductRating,
        numProductRatings,
        isPathwayContent,
        trackingData: {
          entityId: `${entityType}~${entity.id}`,
          entityIndex,
          name: entity.name,
          slug: entity.slug,
          offeringType: productType,
          id: entity.id,
        },
      };
    })
    .filter(Boolean) as CollectionEntity[];

  return {
    collectionId: collection.id,
    title: domainOrSubdomain
      ? getDiscoveryCollectionLabelWithDomainOrSubdomainName(collection, domainOrSubdomain)
      : collection.label,
    entities: parsedEntities,
    collectionLink: collection.linkedCollectionPageMetadata?.url,
    collectionTrackingData: {
      label: collection.label,
      collectionId: collection.id,
      collectionIndex: index,
      collectionName: collection.label,
      // The following are used for MMR ranking.
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      offeringSource: collectionType.toLocaleLowerCase(),
      sectionId: collectionTypeId || collection.id,
      sectionIndex: index,
      listIndex: index,
      listIdentifier: `${collectionType}:${collectionTypeId || collection.id}:${collection.label}`,
      type: collection.id,
    },
    header: collection.metadata?.header,
    footer: collection.metadata?.footer,
  };
};

export const mapCollectionsForCarousel = (
  collections: (
    | DiscoveryCollectionWithMetadata
    | DiscoveryCollectionRatingsReviewWithMetadata
    | DegreeListsV3WithMetadata
    | null
  )[]
): CarouselCollection[] => {
  const carouselCollections = collections?.map((collection, index) => {
    if (collection?.__typename === COLLECTION_TYPENAMES.DISCO_COLLECTIONS_PRODUCT_COLLECTION) {
      return mapDiscoCollectionsForCarousel(collection as DiscoveryCollectionRatingsReviewWithMetadata, index);
    } else {
      return mapPremiumCollectionsForCarousel(collection as DegreeListsV3WithMetadata, index);
    }
  });

  return carouselCollections?.filter(Boolean) as CarouselCollection[];
};

export const getCarouselEntityFromProductCard = (
  productCardEntity: ProductCardEntity,
  index: number,
  collection: CoreCollectionEntityWithMetadata,
  showRatingAndReviews?: boolean
): CollectionEntity | null => {
  const carouselProductType = getProductTypeFromTypename(productCardEntity.marketingProductType);
  const entityType = getEntityTypeFromTypename(productCardEntity.marketingProductType);

  switch (productCardEntity.productTypeAttributes?.__typename) {
    case 'ProductCard_Course': {
      const courseCard = productCardEntity.productTypeAttributes as ProductCardCourse;

      return {
        id: productCardEntity.id,
        name: courseCard.canonical.name,
        slug: courseCard.canonical.slug,
        imageUrl: courseCard.canonical.promoPhotoUrl || '',
        link: collection.metadata?.isEnterprise
          ? getProgramLinkFromProductType(
              collection.metadata?.programSlug || '',
              collection.metadata?.enterpriseCollectionTrackingId,
              courseCard.canonical.slug,
              carouselProductType
            )
          : getLinkFromCarouselTypeName(courseCard.canonical.slug, carouselProductType),
        partners:
          courseCard.canonical.partners?.map((partner) => ({
            id: partner.id,
            logo: partner.squareLogo || '',
            name: partner.name,
          })) || [],
        productType: carouselProductType,
        partnerLogos: courseCard.canonical.partners?.map((partner) => partner.squareLogo).filter(Boolean) || [],
        isPartOfCourseraPlus: !collection.metadata?.isEnterprise ? courseCard.isPartOfCourseraPlus : false,
        statusTags: !collection.metadata?.isEnterprise ? productCardEntity?.badges : null,
        isNewContent: !collection.metadata?.isEnterprise && productCardEntity?.badges?.includes(badgeNewText),
        badges: collection.metadata?.isEnterprise && (
          <React.Fragment>
            {courseCard.canonical.programProductMetadata?.isSelectedForCredit &&
              collection.metadata?.useC4cWordingForCreditBadge && <EnterpriseCreditEligibleBadge />}
            {courseCard.canonical.programProductMetadata?.isExclusive && <EnterpriseExclusiveBadge />}
          </React.Fragment>
        ),
        titleComponent: collection.metadata?.titleComponent,
        forceOpenInNewTab: collection.metadata?.isEnterprise,
        trackingData: {
          entityId: `${entityType}~${productCardEntity.id}`,
          entityIndex: index,
          name: courseCard.canonical.name,
          slug: courseCard.canonical.slug,
          offeringType: carouselProductType,
          id: productCardEntity.id,
        },
        isPathwayContent: !collection.metadata?.isEnterprise && courseCard.isPathwayContent,
        session: courseCard.canonical.enterpriseCustomSessions?.elements?.length
          ? formatSession(
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              courseCard.canonical.enterpriseCustomSessions.elements[0].startsAt,
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              courseCard.canonical.enterpriseCustomSessions.elements[0].endsAt
            )
          : undefined,
        avgProductRating: showRatingAndReviews ? courseCard?.rating : undefined,
        numProductRatings: showRatingAndReviews ? courseCard?.reviewCount : undefined,
      };
    }
    case 'ProductCard_Specialization': {
      const specializationCard = productCardEntity.productTypeAttributes as ProductCardSpecialization;

      return {
        id: productCardEntity.id,
        name: specializationCard.canonical.name,
        slug: specializationCard.canonical.slug,
        imageUrl: specializationCard.canonical.cardImageUrl || '',
        link: collection.metadata?.isEnterprise
          ? getProgramLinkFromProductType(
              collection.metadata?.programSlug || '',
              collection.metadata?.enterpriseCollectionTrackingId,
              specializationCard.canonical.slug,
              carouselProductType
            )
          : getLinkFromCarouselTypeName(specializationCard.canonical.slug, carouselProductType),
        partners:
          specializationCard.canonical.partners?.map((partner) => ({
            id: partner.id,
            logo: partner.squareLogo || '',
            name: partner.name,
          })) || [],
        productType: carouselProductType,
        partnerLogos: specializationCard.canonical.partners?.map((partner) => partner.squareLogo).filter(Boolean) || [],
        coBrandingEnabled: specializationCard.canonical.coBrandingEnabled,
        isPartOfCourseraPlus: !collection.metadata?.isEnterprise ? specializationCard.isPartOfCourseraPlus : false,
        statusTags: !collection.metadata?.isEnterprise ? productCardEntity?.badges : null,
        isNewContent: !collection.metadata?.isEnterprise && productCardEntity?.badges?.includes(badgeNewText),
        badges: collection.metadata?.isEnterprise && (
          <React.Fragment>
            {specializationCard.canonical.programProductMetadata?.isSelectedForCredit &&
              collection.metadata?.useC4cWordingForCreditBadge && <EnterpriseCreditEligibleBadge />}
          </React.Fragment>
        ),
        titleComponent: collection.metadata?.titleComponent,
        forceOpenInNewTab: collection.metadata?.isEnterprise,
        trackingData: {
          entityId: `${entityType}~${productCardEntity.id}`,
          entityIndex: index,
          name: specializationCard.canonical.name,
          slug: specializationCard.canonical.slug,
          offeringType: carouselProductType,
          id: productCardEntity.id,
        },
        isPathwayContent: !collection.metadata?.isEnterprise && specializationCard.isPathwayContent,
        session: specializationCard.canonical.enterpriseCustomSessions?.elements?.length
          ? formatSession(
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              specializationCard.canonical.enterpriseCustomSessions.elements[0].startsAt,
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              specializationCard.canonical.enterpriseCustomSessions.elements[0].endsAt
            )
          : undefined,
        avgProductRating: showRatingAndReviews ? specializationCard?.rating : undefined,
        numProductRatings: showRatingAndReviews ? specializationCard?.reviewCount : undefined,
      };
    }
    case 'ProductCard_Clip': {
      const clipCard = productCardEntity.productTypeAttributes as ProductCardClip;
      const clipSlug = `${clipCard.canonical.course?.slug}/${clipCard.canonical.id.split('~')[1]}`;

      return {
        id: productCardEntity.id,
        name: clipCard.canonical.name,
        slug: clipSlug,
        imageUrl: clipCard.canonical.thumbnail || '',
        link: collection.metadata?.isEnterprise
          ? getProgramLinkFromProductType(
              collection.metadata?.programSlug || '',
              collection.metadata?.enterpriseCollectionTrackingId,
              clipSlug,
              carouselProductType
            )
          : getLinkFromCarouselTypeName(clipSlug, carouselProductType),
        partners:
          clipCard.canonical.course?.partners?.map((partner) => ({
            id: partner.id,
            logo: partner.squareLogo || '',
            name: partner.name,
          })) || [],
        productType: carouselProductType,
        partnerLogos: clipCard.canonical.course?.partners?.map((partner) => partner.squareLogo).filter(Boolean) || [],
        badges: collection.metadata?.isEnterprise && (
          <React.Fragment>
            {clipCard.canonical.views && (
              <CardWatchNow watchNow={{ viewCount: abbreviateLargeNumber(clipCard.canonical.views, _t.getLocale()) }} />
            )}
          </React.Fragment>
        ),
        titleComponent: collection.metadata?.titleComponent,
        forceOpenInNewTab: collection.metadata?.isEnterprise,
        trackingData: {
          entityId: `${carouselProductType}~${productCardEntity.id}`,
          entityIndex: index,
          name: clipCard.canonical.name,
          slug: clipSlug,
          offeringType: carouselProductType,
          id: productCardEntity.id,
        },
        productDuration: clipCard.canonical.duration && moment.duration(clipCard.canonical.duration).humanize(),
      };
    }
    default:
      return null;
  }
};

export const mapCollectionEntityForCarousel = (
  collectionIdWithType: string,
  collection: CoreCollectionEntityWithMetadata,
  additionalTrackingData: {
    collectionIndex: number;
    listIdentifierPrefix?: string;
    carouselRecommender?: Recommender;
    pageConfigComponent?: PageConfigComponentTrackingData;
  }
): CarouselCollection | null => {
  if (!collection?.entities?.elements || collection.entities.elements.length <= 0) {
    return null;
  }

  const splitCollectionId = collectionIdWithType.split(':');
  const { collectionIndex, listIdentifierPrefix, carouselRecommender, pageConfigComponent } = additionalTrackingData;
  // Collection IDs have a format of `COLLECTION_TYPE:ID`, get each piece for tracking purposes.
  const collectionType = splitCollectionId?.[0];
  const collectionTypeId = splitCollectionId?.[1];
  const parsedEntities = collection.entities.elements
    .map((entity, entityIndex) => {
      if (!entity || entity.__typename === 'Collection_Collection') {
        return null;
      }

      const productCardEntity = entity as ProductCardEntity;

      return getCarouselEntityFromProductCard(productCardEntity, entityIndex, collection);
    })
    .filter(Boolean) as CollectionEntity[];
  const listIdentifier = `${collectionType}:${collectionTypeId || collectionIdWithType}:${collection.description}`;

  return {
    collectionId: collectionIdWithType,
    title: collection.description,
    entities: parsedEntities,
    collectionTrackingData: {
      label: collection.description,
      collectionId: collectionIdWithType,
      collectionIndex,
      collectionName: collection.description,
      carouselRecommender,
      pageConfigComponent,
      // The following are used for MMR ranking.
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      offeringSource: collectionType.toLocaleLowerCase(),
      sectionId: collectionTypeId || collectionIdWithType,
      sectionIndex: collectionIndex,
      listIndex: collectionIndex,
      listIdentifier: listIdentifierPrefix ? `${listIdentifierPrefix}:${listIdentifier}` : listIdentifier,
      type: collectionIdWithType,
    },
    header: collection.metadata?.header,
    footer: collection.metadata?.footer,
  };
};

// This is used to map premium collections
export const mapLOHPCollectionForCarousel = (
  collection: EnrichedCatalogResultQuery,
  index: number
): CarouselCollection | null => {
  if (!collection?.entries || collection.entries.length === 0) {
    return null;
  }

  if (
    collection?.__typename === COLLECTION_TYPENAMES.DEGREE_LISTS ||
    collection?.__typename === COLLECTION_TYPENAMES.PREMIUM_PRODUCT_COLLECTION
  ) {
    return mapPremiumCollectionsForCarousel(collection as DegreeListsV3WithMetadata, index);
  } else {
    const entitiesFromEntries = collection.entries.map((entry, entryIndex) => {
      const productType =
        getProductTypeFromTypename(entry.__typename) || getProductTypeFromVariant(entry.productVariant);
      const entityType = getEntityTypeFromTypename(entry.__typename) || getEntityTypeFromVariant(entry.productVariant);

      return {
        id: entry.id,
        name: entry.name,
        slug: entry.slug,
        imageUrl: entry.__typename === COLLECTION_TYPENAMES.COURSES ? entry.photoUrl : entry.logo,
        link: getLinkFromProductVariant(entry.slug, entry.__typename, entry.productVariant),
        partners:
          entry.partners &&
          entry.partners.elements.map((partner) => {
            return {
              id: partner?.id,
              logo: partner?.squareLogo || partner?.logo,
              name: partner?.name,
            };
          }),
        partnerLogos:
          entry.partners &&
          entry.partners.elements.map((partner) => partner?.squareLogo || partner?.logo).filter(Boolean),
        productType,
        trackingData: {
          entityId: `${entityType}~${entry.id}`,
          entityIndex: entryIndex,
          name: entry.name,
          slug: entry.slug,
          offeringType: productType,
          id: entry.id,
        },
      };
    });

    return {
      collectionId: collection.id,
      title: collection.label,
      entities: entitiesFromEntries,
      collectionTrackingData: {
        label: collection.label,
        collectionId: collection.id,
        collectionIndex: index,
        collectionName: collection.label,
      },
    } as CarouselCollection;
  }
};

export const mapProgramCurriculumsForCollectionDisplay = (
  programSlug: string,
  programCurriculums: CollectionType[],
  s12nSessions?: S12nSession[],
  clips?: ClipItem[],
  isUsingC4CWording?: boolean
) => {
  if (!programCurriculums?.length) {
    return [];
  }

  // Create map for clips first so we not need to re-iterate over the clips array for each collection
  const clipsMetadata: Record<string, CurriculumItem> = {};
  clips?.forEach((clip) => {
    // @ts-expect-error Duration: string
    clipsMetadata[clip.id] = {
      ...clip,
      promoPhoto: clip.thumbnail,
      slug: `${clip.course.slug}/${clip.id.split('~')[1]}`,
      partners: {
        elements: clip.partners || [],
      },
      productType: getProductTypeFromTypename(clip.__typename),
      entityType: getEntityTypeFromTypename(clip.__typename),
    };
  });

  const carouselCollections = programCurriculums.map((collection) => {
    if (!collection?.items?.length) {
      return null;
    }

    // Create a map for all product items with the product id as the key and includes the product type and associated s12n session id as values
    const itemsMetadata: Record<
      string,
      CurriculumItem & {
        associatedS12nSessionId?: string | null;
        isSelectedForCredit?: boolean;
        isExclusive?: boolean;
        associatedCourseSession?: CourseSession;
      }
    > = {
      ...clipsMetadata,
    };
    const courses = filterExistsOrDefault(collection.courses?.elements);
    const s12ns = filterExistsOrDefault(collection.s12ns?.elements);
    const productConfigs = filterExistsOrDefault(collection.enterpriseProductConfigurations);
    const associatedCourseSessions = filterExistsOrDefault(collection.associatedSessions?.elements);

    courses.forEach((course) => {
      itemsMetadata[tupleToStringKey(['VerifiedCertificate', course.id])] = {
        ...course,
        productType: getProductTypeFromTypename(
          course.courseTypeMetadata?.courseTypeMetadata.__typename || course.__typename
        ),
        entityType: getEntityTypeFromTypename(
          course.courseTypeMetadata?.courseTypeMetadata.__typename || course.__typename
        ),
      };
    });

    s12ns.forEach((s12n) => {
      itemsMetadata[tupleToStringKey(['Specialization', s12n.id])] = {
        ...s12n,
        productType: getProductTypeFromVariant(s12n.productVariant),
        entityType: getEntityTypeFromVariant(s12n.productVariant),
      };
    });

    productConfigs.forEach((product) => {
      // The product id in the productConfigs object contains the collection id as a prefix which we should remove
      const productId = product.id.substring(product.id.indexOf('~') + 1);

      // Add these data attributes to the existing item metadata
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      itemsMetadata[productId] = {
        ...itemsMetadata[productId],
        associatedS12nSessionId: product?.metadataType.specialization?.associatedSessionId,
        isSelectedForCredit:
          product?.metadataType.course?.isSelectedForCredit ||
          product?.metadataType.specialization?.isSelectedForCredit ||
          false,
        isExclusive:
          product?.metadataType.course?.isExclusive || product?.metadataType.specialization?.isExclusive || false,
      };
    });

    associatedCourseSessions.forEach((associatedCourseSession) => {
      if (!associatedCourseSession?.courseId) {
        return;
      }

      const productId = tupleToStringKey(['VerifiedCertificate', associatedCourseSession.courseId]);

      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      itemsMetadata[productId] = {
        ...itemsMetadata[productId],
        associatedCourseSession,
      };
    });

    const parsedEntites = collection.items?.slice(0, MAX_ITEMS_PER_ENTERPRISE_CAROUSEL).map((item, itemIndex) => {
      const productId = item.clipId ? `${item.clipId.courseId}~${item.clipId.itemId}` : item.productId;
      const metadata = itemsMetadata[productId];

      if (!metadata) {
        return null;
      }
      // Obtain the session for the s12n through the associated session id
      const session = productId.includes('Specialization')
        ? s12nSessions?.find((s12nSession) => s12nSession?.id === metadata.associatedS12nSessionId)
        : metadata.associatedCourseSession;

      return {
        id: productId,
        name: metadata.name,
        slug: metadata.slug,
        imageUrl: metadata.promoPhoto,
        link: getProgramLinkFromProductType(
          programSlug,
          collection.collectionTrackingId,
          metadata.slug,
          metadata.productType
        ),
        partners: metadata.partners?.elements.map((partner) => ({
          id: partner?.id,
          name: partner?.name,
          logo: partner?.squareLogo,
        })),
        partnerLogos: metadata.partners?.elements.map((partner) => partner?.squareLogo),
        productType: metadata.productType,
        badges: (
          <React.Fragment>
            {metadata.isExclusive && <EnterpriseExclusiveBadge />}
            {metadata.isSelectedForCredit && isUsingC4CWording && <EnterpriseCreditEligibleBadge />}
            {metadata.views && (
              <CardWatchNow watchNow={{ viewCount: abbreviateLargeNumber(metadata.views, _t.getLocale()) }} />
            )}
          </React.Fragment>
        ),
        session: session && formatSession(session?.startsAt, session?.endsAt),
        productDuration: metadata.duration && moment.duration(metadata.duration).humanize(),
        titleComponent: collection.entityTitleComponent,
        forceOpenInNewTab: true,
        trackingData: {
          entityId: `${metadata.entityType}~${metadata.id}`,
          entityIndex: itemIndex,
          name: metadata.name,
          slug: metadata.slug,
          offeringType: metadata.productType,
          id: metadata.id,
        },
      } as CollectionEntity | null;
    });

    const filteredEntities = parsedEntites?.filter(Boolean) as CollectionEntity[];

    if (!filteredEntities.length) {
      return null;
    }

    return {
      collectionId: collection.id,
      title: collection.title,
      entities: filteredEntities,
      collectionTrackingData: {
        collectionId: collection.collectionTrackingId ?? collection.trackId,
        collectionName: collection.title,
      },
      collectionType: collection.type,
      description: collection.description,
    };
  });

  return carouselCollections.filter(Boolean) as Array<CarouselCollection>;
};
